/**
 * O selo de estado para o que vem do BANCO.
 *
 * POR QUE `FiscalStateBadge` NÃO BASTA
 *
 *   `FiscalStateBadge` recebe `FiscalDocumentState`, a lista fechada do domínio
 *   compartilhado — doze estados que valem para NF-e e NFS-e.
 *
 *   O banco tem mais dois, criados por migrations da NFS-e depois daquela lista:
 *
 *     `awaiting_distribution`  o canal aceitou, o documento oficial ainda não
 *                              chegou pelo ADN (0026)
 *     `substituted`            a nota foi substituída por outra
 *
 *   Alargar o tipo do domínio para caber os dois seria dizer que uma NF-e pode
 *   estar "aguardando distribuição", e ela não pode — não existe ADN para NF-e.
 *   São estados de NFS-e, e o domínio compartilhado está certo em não tê-los.
 *
 * O ESTADO DESCONHECIDO APARECE COMO DESCONHECIDO
 *
 *   Um status que não esteja em nenhuma das duas listas é renderizado com o
 *   texto cru e um selo neutro. A alternativa — cair num `default` bonito, ou
 *   mostrar "autorizado" porque é o mais comum — faria a tela afirmar sobre um
 *   documento algo que ninguém verificou.
 *
 *   É o mesmo princípio do `?? 'giap'` que o guard de provedor proíbe no
 *   núcleo: valor padrão em campo fiscal é afirmação sem procedência.
 */

import { FISCAL_DOCUMENT_STATES, type FiscalDocumentState } from '@optefiscal/fiscal-domain';
import { FiscalStateBadge } from './FiscalStateBadge.js';

/** Os estados que só a NFS-e tem, com o significado que a migration lhes deu. */
const ESTADOS_NFSE: Readonly<Record<string, { label: string; meaning: string; tone: string }>> =
  Object.freeze({
    awaiting_distribution: {
      label: 'Aguardando distribuição',
      meaning:
        'O canal aceitou o pedido. O documento fiscal oficial ainda não chegou pela distribuição '
        + 'do ADN — o canal responder não é o documento existir.',
      tone: 'warning',
    },
    substituted: {
      label: 'Substituída',
      meaning: 'Esta nota foi substituída por outra.',
      tone: 'neutral',
    },
  });

function eDoDominio(status: string): status is FiscalDocumentState {
  return (FISCAL_DOCUMENT_STATES as readonly string[]).includes(status);
}

export function DocumentStatusBadge({ status, code }: { status: string; code?: string | null }) {
  if (eDoDominio(status)) {
    return <FiscalStateBadge state={status} {...(code ? { code } : {})} />;
  }

  const nfse = ESTADOS_NFSE[status];
  if (nfse !== undefined) {
    return (
      <span className={`of-state of-state--${nfse.tone}`} title={nfse.meaning}>
        <span>{nfse.label}</span>
        {code ? <span className="of-state__code">{code}</span> : null}
      </span>
    );
  }

  return (
    <span
      className="of-state of-state--neutral"
      title="Estado não classificado na interface. O texto é o valor persistido, sem tradução."
    >
      <span className="of-mono">{status}</span>
      {code ? <span className="of-state__code">{code}</span> : null}
    </span>
  );
}
